import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: the only light here is the particles' own, and the
// subject is which of them is sharp. A rig would have nothing to light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'focus',
  family: 'photographic',
  danceability: 0.20,
  requiresTier: 1,
  energyLevel: 0.25,
  bpmMin: 50,
  bpmMax: 140,
  movement: 0.25,
  tonality: 0.90,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Focus: five stacked planes and a real depth of field, whose plane of
// sharpness crosses the scene on every phrase. Everywhere else in this library
// the event is a movement; here nothing moves much and what changes is WHAT IS
// SHARP. The blur is the subject, not a degradation of one.
//
// DEPTH LIVES IN THE ALPHA CHANNEL, not in a depth texture. Nothing in this
// project has ever used a DepthTexture, so its behaviour on the Pi's V3D and on
// the television is unknown; and it is not needed. Every plane here is at a
// KNOWN z, so each material simply writes its own view depth into alpha while
// the depth buffer resolves occlusion. Exact per fragment, portable, and with
// nothing to linearise afterwards. The cost is that nothing may blend: every
// material renders with NoBlending and cuts its own shape with discard, since a
// blended alpha would be a blend of two depths and mean nothing.
//
// THE BOKEH IS THE WHOLE POINT. A gaussian blur would sink this piece. What a
// defocused highlight shows is the SHAPE OF THE APERTURE, so the blur samples a
// Poisson DISC with UNIFORM weights - it is the uniformity that gives the hard
// round edge - and at tier 2 that disc is squeezed into a hexagon, which reads
// as a six-bladed lens the instant you see it.

const PLANE_Z = [5.5, 2.0, 0.0, -3.5, -9.0];
const CAMERA_Z = 13.0;
const FOV = 42;
// Alpha is 8-bit, so the depth it carries is quantised to 1/255 of this range.
// The planes are 3.5 units apart at the closest, which is 42 steps - far more
// than the leak-rejection bias needs to tell one plane from the next.
const DEPTH_MIN = 4.0;
const DEPTH_MAX = 26.0;

// Halved. At 120/200 the frame was a TEXTURE of bokeh with no gaps, and a
// depth of field you cannot see between is not a depth of field.
const NEAR_PARTICLES = 60;
const FAR_PARTICLES = 100;
// Log-uniform over a factor of four, so a single plane carries small and large
// sources together. Drawing them all from one narrow range made every disc the
// same size and flattened the plane into a wall.
const SIZE_SPREAD = 4.0;
const FOREGROUND_SHAPES = 5;
const BACKGROUND_GLOWS = 3;   // wide and spaced, with black between them

// The brief's own formula is coc = |d - f| / d * aperture, and it is easy to
// be an order of magnitude out on the aperture without noticing: at 3.4 the
// farthest plane came to 9/22 * 3.4 = 1.39, forty-three times MAX_COC, so
// every depth more than 12cm off the focal plane clamped to maximum blur and
// the depth of field had no gradient at all. ?focuscoc=1 was white everywhere.
// At 0.085 the neighbouring plane sits at two thirds of maximum and the far
// one clamps, which is the thin depth of field the piece wants; the +-45%
// band on bands.low then runs it from two or three planes sharp to one.
const APERTURE_BASE = 0.050;
// Wide enough that the two ends mean something: at the small end a
// neighbouring plane sits near a fifth of maximum blur - readably sharp, two
// or three planes at once - and at the large end near two thirds, which is the
// single-plane look. At the old 0.085 base the NEIGHBOURS were already at
// 64-75% whatever the band did, so nothing was ever sharp but the focal plane
// itself and the depth of field could not be read at all.
const APERTURE_BAND = 0.55;     // bands.low
const APERTURE_TAU = 2.2;       // the aperture BREATHES; it does not twitch
// In screen heights. Big enough that a defocused highlight is unmistakably a
// disc, small enough that the discs do not merge into a single wash.
const MAX_COC = 0.024;
const LEAK_BIAS = 0.9;          // world units, see the blur shader
const DRIFT_BASE = 0.055;
const DRIFT_BAND = 0.6;         // bands.mid
const SPARKLE_BAND = 0.55;      // bands.high
const SAFE_FLOOR = 0.55;

const FOCUS_BEATS = 2.6;        // a lens glides; 2 to 3 beats per the brief
const RACK_BEATS = 1.0;         // the drop's violent pull, one beat end to end
const DEFAULT_BPM = 84;

const TIER = {
  // Tier 0 is unavailable by contract (requiresTier: 1).
  1: { samples: 16, hexagonal: false, sceneScale: 0.85, blurScale: 0.5 },
  2: { samples: 32, hexagonal: true, sceneScale: 1.0, blurScale: 0.5 }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// Deterministic hash, so the scene is the same shape every time it is built.
function rng(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// Mitchell's best-candidate on the unit disc: cheap, deterministic, and gives a
// far more even covering than plain random, which is what stops the bokeh from
// showing clumps. Generated once at module load and baked into the shader
// source as a constant array, which is what the brief asks for.
function poissonDisc(count, seed) {
  const rand = rng(seed);
  const pts = [];
  for (let i = 0; i < count; i++) {
    let best = null, bestD = -1;
    const tries = 12 + i * 2;
    for (let t = 0; t < tries; t++) {
      const a = rand() * Math.PI * 2;
      const r = Math.sqrt(rand());
      const p = [Math.cos(a) * r, Math.sin(a) * r];
      let d = 1e9;
      for (let k = 0; k < pts.length; k++) {
        const dx = p[0] - pts[k][0], dy = p[1] - pts[k][1];
        d = Math.min(d, dx * dx + dy * dy);
      }
      if (d > bestD) { bestD = d; best = p; }
    }
    pts.push(best);
  }
  return pts;
}

// Squeezes the disc into a regular hexagon by scaling each sample's radius to
// the hexagon's own edge at that angle. Six blades, which is what most lenses
// with a visible bokeh shape actually have.
function toHexagon(pts) {
  return pts.map(function (p) {
    const a = Math.atan2(p[1], p[0]);
    const r = Math.hypot(p[0], p[1]);
    const sector = ((a % (Math.PI / 3)) + Math.PI / 3) % (Math.PI / 3);
    const edge = Math.cos(Math.PI / 6) / Math.cos(sector - Math.PI / 6);
    return [Math.cos(a) * r * edge, Math.sin(a) * r * edge];
  });
}

// Emitted as straight-line calls rather than as a const array: `vec2 D[N] =
// vec2[N](...)` is GLSL ES 3.00 syntax and three compiles ShaderMaterial as
// ES 1.00, where a const array cannot be initialised at all. Unrolled, the tap
// body is about fifteen instructions - small enough that the Pi's driver, which
// compiles in time proportional to program size, is untroubled by 32 of them.
function discTaps(count, hexagonal) {
  let pts = poissonDisc(count, 0x5eed + count);
  if (hexagonal) pts = toHexagon(pts);
  return pts.map(function (p) {
    return `    tap(vec2(${p[0].toFixed(5)}, ${p[1].toFixed(5)}), vUv, radius, cDepth, coc, sum, weight);`;
  }).join('\n');
}

const QUAD_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// Shared by every material in the inner scene: turn a view depth into the alpha
// byte the blur pass reads back.
const DEPTH_ENCODE = `
  uniform float uDepthMin;
  uniform float uDepthSpan;
  float encodeDepth(float viewDepth) {
    return clamp((viewDepth - uDepthMin) / uDepthSpan, 0.0, 1.0);
  }
`;

// --- particles: the things that become bokeh, so small and bright by contract
const PARTICLE_VERTEX = `
  precision mediump float;
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aPhase;
  uniform float uTime;
  uniform float uPixelScale;
  uniform float uSparkle;
  varying vec3 vColor;
  varying float vDepth;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDepth = -mv.z;
    // Twinkle, which is what makes the bokeh discs breathe rather than sit.
    float tw = 0.72 + 0.28 * sin(uTime * (1.1 + aPhase * 2.3) + aPhase * 6.28);
    vColor = aColor * (1.0 + uSparkle * (tw - 0.86) * 2.0);
    gl_PointSize = aSize * uPixelScale / max(vDepth, 0.5);
    gl_Position = projectionMatrix * mv;
  }
`;

const PARTICLE_FRAGMENT = `
  precision mediump float;
${DEPTH_ENCODE}
  varying vec3 vColor;
  varying float vDepth;

  void main() {
    // Cut the disc with discard, and fade the COLOUR rather than the alpha -
    // alpha is carrying depth here and has no room for coverage.
    vec2 d = gl_PointCoord - vec2(0.5);
    float r = length(d) * 2.0;
    if (r > 1.0) discard;
    // HARD EDGED, deliberately. Alpha is carrying depth, so nothing here can
    // blend: a softened rim is drawn as DARK over the background it occludes,
    // and every particle ended up with a black ring baked into the source that
    // the blur then spread into a visible outline around each bokeh. A hard
    // edge on a sprite this small is invisible sharp and perfect once blurred.
    float core = 1.0;
    // Pushed past 1.0 on purpose. The blur's extended-luminance step keys off
    // how close a sample is to white - a particle that peaks at 0.7 expands to
    // 2.3, one that reaches 1.0 expands to 50 - so the cores have to clip if
    // the defocused version is to stay bright instead of going grey.
    gl_FragColor = vec4(vColor * core * 1.15, encodeDepth(vDepth));
  }
`;

// --- the near silhouettes: almost black, cut by the frame, there for scale
const SHAPE_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  varying float vDepth;
  void main() {
    vUv = uv;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const SHAPE_FRAGMENT = `
  precision mediump float;
${DEPTH_ENCODE}
  uniform vec3 uRim;
  uniform float uSeed;
  varying vec2 vUv;
  varying float vDepth;

  float hash1(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash1(i), hash1(i + vec2(1.0, 0.0)), f.x),
               mix(hash1(i + vec2(0.0, 1.0)), hash1(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  void main() {
    // A frond-ish lobe: a radial profile pushed around by noise, so the five
    // shapes read as foliage rather than as five ellipses.
    vec2 p = vUv * 2.0 - 1.0;
    float a = atan(p.y, p.x);
    float r = length(p);
    float wob = vnoise(vec2(a * 2.4 + uSeed, uSeed * 1.7)) * 0.45
              + vnoise(vec2(a * 6.1 + uSeed * 3.0, 2.0)) * 0.18;
    float edge = 0.62 + wob;
    if (r > edge) discard;
    // Near-black with a faint rim: it must frame, never attract.
    float rim = smoothstep(edge - 0.16, edge, r);
    vec3 col = uRim * (rim * 0.30);
    gl_FragColor = vec4(col, encodeDepth(vDepth));
  }
`;

// --- the far wash
const BACK_FRAGMENT = `
  precision mediump float;
${DEPTH_ENCODE}
  uniform vec3 uTint;
  uniform vec3 uGlowA;
  uniform vec3 uGlowB;
  varying vec2 vUv;
  varying float vDepth;

  float hash1(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  void main() {
    vec2 p = vUv * 2.0 - 1.0;

    // A DARK GRADIENT, near-black at the top and never past 0.12 at the
    // bottom. A bokeh only exists against dark - the contrast is what makes it
    // read as light - and the previous version filled the frame with a flat
    // wash that made the whole image look dirty.
    float down = clamp(0.5 - p.y * 0.5, 0.0, 1.0);
    vec3 col = uTint * (0.012 + 0.108 * down * down);

    // Three WIDE, WELL SPACED sources with black between them, not four
    // overlapping ones that merged into a full-frame flat. Fixed positions,
    // pushed apart, and a falloff tight enough that the gaps stay black.
    vec2 c0 = vec2(-0.62, 0.34), c1 = vec2(0.58, -0.18), c2 = vec2(0.06, 0.74);
    float g0 = exp(-dot(p - c0, p - c0) * 5.5);
    float g1 = exp(-dot(p - c1, p - c1) * 4.2);
    float g2 = exp(-dot(p - c2, p - c2) * 7.0);
    col += uGlowA * g0 * 0.085;
    col += uGlowB * g1 * 0.075;
    col += mix(uGlowA, uGlowB, 0.5) * g2 * 0.055;

    // Dither, mandatory: this is a dark near-flat ramp, which is exactly where
    // eight bits band.
    col += (hash1(gl_FragCoord.xy) - 0.5) * (2.4 / 255.0);
    gl_FragColor = vec4(col, encodeDepth(vDepth));
  }
`;

// --- the cover plane, at z = 0, sized so the DOM cover lands exactly on it
const COVER_FRAGMENT = `
  precision mediump float;
${DEPTH_ENCODE}
  uniform sampler2D uArtwork;
  uniform float uHasArtwork;
  uniform vec3 uFallback;
  uniform float uLift;
  varying vec2 vUv;
  varying float vDepth;

  void main() {
    vec3 col = uHasArtwork > 0.5 ? texture2D(uArtwork, vUv).rgb : uFallback;
    gl_FragColor = vec4(col * uLift, encodeDepth(vDepth));
  }
`;

// --- THE BLUR. Half resolution, uniform weights on a Poisson disc.
function blurFragment(samples, hexagonal) {
  return `
  precision mediump float;
  uniform sampler2D uScene;
  uniform vec2 uTexel;
  uniform float uFocusDepth;
  uniform float uAperture;
  uniform float uMaxCoc;
  uniform float uAspect;
  uniform float uDepthMin;
  uniform float uDepthSpan;
  uniform float uLeakBias;
  varying vec2 vUv;

  float depthAt(vec4 t) { return uDepthMin + t.a * uDepthSpan; }

  float cocAt(float depth) {
    float c = abs(depth - uFocusDepth) / max(depth, 0.1);
    return clamp(c * uAperture, 0.0, uMaxCoc);
  }

  // EXTENDED LUMINANCE. A defocused highlight in a photograph keeps its
  // intensity as it spreads; averaged naively it just goes grey, which is the
  // single thing that makes a blur read as a word processor's rather than a
  // lens's. Expanding before the average and contracting after lets one bright
  // sample dominate its whole disc.
  vec3 expand(vec3 c) { return c / (1.0 - min(c, vec3(0.98))); }
  vec3 contract(vec3 c) { return c / (1.0 + c); }

  // Per-pixel rotation of the whole sample set. With 32 taps on a disc 21px
  // across the spacing is about 4px, which is the size of a sharp particle -
  // so a background pixel finds it at only one or two tap positions, and a
  // small bright source came out replicated as a CLUSTER OF DOTS in the shape
  // of the Poisson pattern. Turning the disc by a different angle at every
  // pixel converts that structure into grain, which at this scale is invisible.
  mat2 uJitter;

  void tap(vec2 off, vec2 uv, vec2 radius, float cDepth, float cCoc,
           inout vec3 sum, inout float weight) {
    off = uJitter * off;
    vec4 s = texture2D(uScene, uv + off * radius);
    float sDepth = uDepthMin + s.a * uDepthSpan;
    // FOREGROUND LEAK, but not with a blunt instrument. A sample BEHIND the
    // centre always belongs to its blur. A sample in FRONT only belongs if its
    // OWN circle of confusion is wide enough to reach this pixel: a sharp
    // object cannot spill (that is the halo the brief warns about), while a
    // defocused one must, because spilling is exactly what defocus is.
    // Rejecting every nearer sample outright - the first version here - stops
    // a far particle's disc from ever covering the background behind it, and
    // every defocused highlight came out ringed in black.
    float reach = length(off) * cCoc;
    float sCoc = cocAt(sDepth);
    float ok = sDepth >= cDepth - uLeakBias ? 1.0 : step(reach, sCoc);
    sum += expand(s.rgb) * ok;
    weight += ok;
  }

  void main() {
    vec4 centre = texture2D(uScene, vUv);
    float cDepth = depthAt(centre);
    float coc = cocAt(cDepth);

    vec3 sum = expand(centre.rgb);
    float weight = 1.0;
    vec2 radius = vec2(coc / uAspect, coc);

    vec3 hq = fract(vec3(gl_FragCoord.xyx) * vec3(0.1031, 0.1030, 0.0973));
    hq += dot(hq, hq.yzx + 33.33);
    float ang = fract((hq.x + hq.y) * hq.z) * 6.28318530718;
    float ca = cos(ang), sa = sin(ang);
    uJitter = mat2(ca, -sa, sa, ca);

${discTaps(samples, hexagonal)}
    gl_FragColor = vec4(contract(sum / weight), centre.a);
  }
`;
}

// --- composite: sharp where the circle of confusion is small, blurred where it
// is not, and the safe zone applied once at the end.
const COMPOSITE_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uScene;
  uniform sampler2D uBlur;
  uniform float uFocusDepth;
  uniform float uAperture;
  uniform float uMaxCoc;
  uniform float uDepthMin;
  uniform float uDepthSpan;
  uniform float uCocDebug;
  uniform float uHeightPx;
  uniform highp float uAspect;
  uniform highp vec2 uSafeCenter;
  uniform highp vec2 uSafeHalfSize;
  varying vec2 vUv;

  void main() {
    vec4 sharp = texture2D(uScene, vUv);
    float depth = uDepthMin + sharp.a * uDepthSpan;
    float coc = clamp(abs(depth - uFocusDepth) / max(depth, 0.1) * uAperture, 0.0, uMaxCoc);
    float cocPx = coc * uHeightPx;

    if (uCocDebug > 0.5) {
      // Held to 0.7 so it stays under the composer's bloom threshold. At full
      // range the readout blooms into itself and reads 245 where the true
      // ratio is 0.01 - a debug view that lies is worse than none.
      gl_FragColor = vec4(vec3(cocPx / max(uMaxCoc * uHeightPx, 1.0) * 0.7), 1.0);
      return;
    }

    // The half-resolution blur is only trusted where the blur is actually
    // strong; under a couple of pixels the sharp render is both cheaper and
    // better, and the ramp between them is wide enough never to show a seam.
    vec3 col = mix(sharp.rgb, texture2D(uBlur, vUv).rgb, smoothstep(0.6, 3.0, cocPx));

    // Circular, not the shared chunk's box: a rectangle of dimming is visible
    // as an edge on a soft photographic image, which is the one thing this
    // piece cannot have.
    vec2 sp = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    float rad = max(max(uSafeHalfSize.x, uSafeHalfSize.y), 0.001);
    float e = length(sp - uSafeCenter) / rad;
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.55, 1.85, e));

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class FocusVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : (tier < 1 ? 1 : DEFAULT_TIER);

    const fp = parseInt(urlParam('focusplane'), 10);
    this._forcedPlane = Number.isFinite(fp) && fp >= 0 && fp < PLANE_Z.length ? fp : null;
    const fa = parseFloat(urlParam('focusaperture'));
    this._forcedAperture = Number.isFinite(fa) && fa > 0 ? fa : null;
    this._cocDebug = urlParam('focuscoc') === '1';
    this._forceRack = urlParam('focusrack') === '1';

    this._time = 0;
    this._paused = false;
    this._planeIndex = 2;          // start on the cover
    this._fromDepth = CAMERA_Z - PLANE_Z[2];
    this._toDepth = this._fromDepth;
    this._focusT = 1;
    this._focusDur = 1;
    this._lastPhrase = null;
    this._dropArmed = false;
    this._aperture = APERTURE_BASE;
    this._drift = DRIFT_BASE;
    this._driftPhase = 0;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;

    this._inner = new THREE.Scene();
    this._innerCam = new THREE.PerspectiveCamera(FOV, 1, 0.5, 60);
    this._innerCam.position.set(0, 0, CAMERA_Z);

    const depthUniforms = {
      uDepthMin: { value: DEPTH_MIN },
      uDepthSpan: { value: DEPTH_MAX - DEPTH_MIN }
    };
    this._depthUniforms = depthUniforms;

    // Every material in the inner scene: no blending, depth written, shape cut
    // with discard. See the header for why alpha may not be blended.
    const solid = { transparent: false, blending: THREE.NoBlending, depthTest: true, depthWrite: true };

    // --- background
    this._backUniforms = Object.assign({
      uTint: { value: new THREE.Color(0x121a2a) },
      uGlowA: { value: new THREE.Color(0x3d6ea8) },
      uGlowB: { value: new THREE.Color(0x9a6bb5) }
    }, depthUniforms);
    this._back = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial(
      Object.assign({ uniforms: this._backUniforms, vertexShader: SHAPE_VERTEX, fragmentShader: BACK_FRAGMENT }, solid)));
    this._back.position.z = PLANE_Z[4];
    this._inner.add(this._back);

    // --- cover
    this._coverUniforms = Object.assign({
      uArtwork: { value: null },
      uHasArtwork: { value: 0 },
      uFallback: { value: new THREE.Color(0x2a3550) },
      uLift: { value: 1.12 }
    }, depthUniforms);
    this._cover = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial(
      Object.assign({ uniforms: this._coverUniforms, vertexShader: SHAPE_VERTEX, fragmentShader: COVER_FRAGMENT }, solid)));
    this._cover.position.z = PLANE_Z[2];
    this._inner.add(this._cover);

    // --- two particle fields
    this._particleUniforms = Object.assign({
      uTime: { value: 0 },
      uPixelScale: { value: 300 },
      uSparkle: { value: 0.5 }
    }, depthUniforms);
    const particleMat = new THREE.ShaderMaterial(Object.assign({
      uniforms: this._particleUniforms,
      vertexShader: PARTICLE_VERTEX,
      fragmentShader: PARTICLE_FRAGMENT
    }, solid));
    this._near = this._makeField(NEAR_PARTICLES, PLANE_Z[1], 7.0, 1.45, 0x1234, particleMat);
    // 0.78, not smaller. The far field is meant to be the smaller and denser of
    // the two, and it is - half the near field's size - but there is a floor:
    // a source whose radius falls under the gather's tap spacing (R/sqrt(N),
    // about 2.8px here) is found by one tap instead of several and defocuses
    // into a cluster of speckles rather than a disc.
    this._far = this._makeField(FAR_PARTICLES, PLANE_Z[3], 11.0, 0.78, 0x9876, particleMat);
    this._inner.add(this._near.points);
    this._inner.add(this._far.points);

    // --- foreground silhouettes
    this._shapes = [];
    const rand = rng(0xbeef);
    for (let i = 0; i < FOREGROUND_SHAPES; i++) {
      const u = Object.assign({
        uRim: { value: new THREE.Color(0x4a6a8c) },
        uSeed: { value: rand() * 40 }
      }, depthUniforms);
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial(
        Object.assign({ uniforms: u, vertexShader: SHAPE_VERTEX, fragmentShader: SHAPE_FRAGMENT }, solid)));
      // AROUND THE RIM, not across the frame. At this plane's depth the
      // visible frame is under six units tall, so a shape a few units wide
      // placed near the origin simply fills the screen with black - which is
      // what the first build did. Each one is anchored just OUTSIDE the edge
      // at its own angle and reaches in, which is what "cut by the frame"
      // means. Actual placement happens in _fitCover, where the aspect is
      // known; here we only choose where each one lives.
      m.userData.angle = (i / FOREGROUND_SHAPES) * Math.PI * 2 + rand() * 0.8;
      m.userData.radius = 1.02 + rand() * 0.30;
      m.userData.span = 0.52 + rand() * 0.46;     // in half-heights
      m.rotation.z = rand() * Math.PI * 2;
      m.userData.drift = [(rand() * 2 - 1), (rand() * 2 - 1), 0.5 + rand()];
      this._shapes.push(m);
      this._inner.add(m);
    }

    // --- targets
    this._sceneBuf = new FeedbackBuffer({ scale: layout.sceneScale, single: true, depth: true });
    this._blurBuf = new FeedbackBuffer({ scale: layout.sceneScale * layout.blurScale, single: true });

    this._blurUniforms = {
      uScene: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uFocusDepth: { value: this._toDepth },
      uAperture: { value: APERTURE_BASE },
      uMaxCoc: { value: MAX_COC },
      uAspect: { value: 1 },
      uDepthMin: { value: DEPTH_MIN },
      uDepthSpan: { value: DEPTH_MAX - DEPTH_MIN },
      uLeakBias: { value: LEAK_BIAS }
    };
    this._blurMat = new THREE.ShaderMaterial({
      uniforms: this._blurUniforms,
      vertexShader: QUAD_VERTEX,
      fragmentShader: blurFragment(layout.samples, layout.hexagonal),
      depthTest: false, depthWrite: false
    });
    this._passScene = new THREE.Scene();
    this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._blurMat);
    this._passQuad.frustumCulled = false;
    this._passScene.add(this._passQuad);
    this._passCam = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._passCam.position.z = 1;

    // --- the thing the main scene actually draws
    this._compositeUniforms = Object.assign({
      uScene: { value: null },
      uBlur: { value: null },
      uFocusDepth: { value: this._toDepth },
      uAperture: { value: APERTURE_BASE },
      uMaxCoc: { value: MAX_COC },
      uDepthMin: { value: DEPTH_MIN },
      uDepthSpan: { value: DEPTH_MAX - DEPTH_MIN },
      uCocDebug: { value: this._cocDebug ? 1 : 0 },
      uHeightPx: { value: 720 }
    }, {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize
    });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._compositeUniforms,
      vertexShader: QUAD_VERTEX,
      fragmentShader: COMPOSITE_FRAGMENT,
      depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);
  }

  _makeField(count, z, spread, sizeScale, seed, material) {
    const rand = rng(seed);
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const size = new Float32Array(count);
    const col = new Float32Array(count * 3);
    const phase = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (rand() * 2 - 1) * spread;
      pos[i * 3 + 1] = (rand() * 2 - 1) * spread * 0.62;
      pos[i * 3 + 2] = z + (rand() * 2 - 1) * 0.35;
      // SMALL and BRIGHT. A wide dull particle makes no bokeh worth looking
      // at - and the first build got this badly wrong: at 0.55 units the
      // sharp sprite was already 34px across and the blur turned the frame
      // into a wall of overlapping discs with no dark between them. A few
      // pixels sharp is what becomes a clean disc when it goes out.
      // Log-uniform across SIZE_SPREAD, which puts as many large sources as
      // small ones on every plane. The floor matters: the gather's tap spacing
      // is R/sqrt(N) - about 3.7px here - and a source smaller than that is
      // found by one tap and defocuses into speckles rather than a disc.
      size[i] = 0.30 * Math.pow(SIZE_SPREAD, rand()) * sizeScale;
      phase[i] = rand();
      col[i * 3] = 1; col[i * 3 + 1] = 1; col[i * 3 + 2] = 1;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    geo.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    const points = new THREE.Points(geo, material);
    points.frustumCulled = false;
    return { points: points, geo: geo, count: count, home: pos.slice(), rand: rand };
  }

  // A lens GLIDES. Smootherstep, which starts and ends with zero velocity AND
  // zero acceleration - a plain smoothstep still arrives with a visible stop.
  _focusDepth() {
    const t = clamp01(this._focusT);
    const e = t * t * t * (t * (t * 6 - 15) + 10);
    return this._fromDepth + (this._toDepth - this._fromDepth) * e;
  }

  _startFocus(planeIndex, beats, bpm) {
    this._fromDepth = this._focusDepth();
    this._planeIndex = planeIndex;
    this._toDepth = CAMERA_Z - PLANE_Z[planeIndex];
    this._focusT = 0;
    this._focusDur = Math.max(0.25, beats * 60 / Math.max(bpm, 40));
  }

  onTrackChange(profile) {
    this._lastPhrase = null;
    this._dropArmed = false;
    if (profile && profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
  }

  onPlayPause(state) { this._paused = state !== 'PLAYING'; }

  update(deltaTime, profile) {
    // Pause freezes everything, INCLUDING a focus transition in flight.
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const dyn = director && director.character && director.character.dynamics != null
      ? clamp01(director.character.dynamics) : 0.5;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || DEFAULT_BPM;

    // --- the aperture breathes; it never twitches
    const wanted = this._forcedAperture != null
      ? this._forcedAperture
      : APERTURE_BASE * (1 + (low - 0.5) * 2 * APERTURE_BAND);
    const k = 1 - Math.exp(-deltaTime / APERTURE_TAU);
    this._aperture += (wanted - this._aperture) * k;

    // bands.mid drives the drift's SPEED, which is what the brief asks for -
    // it used to scale the AMPLITUDE of a sin(t) and that is the tremble. An
    // amplitude read fresh every frame from a band that pulses on the beat
    // moves every plane the instant the band moves: the picture twitched in
    // time, and a viewer could read the tempo off it. Integrating a phase
    // instead means the band changes how FAST the planes wander and never
    // where they are, so nothing can jump however sharply the band does.
    this._driftPhase += deltaTime * (1 + (mid - 0.5) * 2 * DRIFT_BAND);
    this._particleUniforms.uSparkle.value = 0.25 + SPARKLE_BAND * high;

    // --- the plane of sharpness
    if (this._forcedPlane != null) {
      this._planeIndex = this._forcedPlane;
      this._fromDepth = this._toDepth = CAMERA_Z - PLANE_Z[this._forcedPlane];
      this._focusT = 1;
    } else {
      const phrase = profile.phraseIndex;
      if (phrase != null && phrase !== this._lastPhrase) {
        if (this._lastPhrase != null) {
          // Drawn among the five, never the same one twice running. The
          // amplitude of the move follows dynamics: on a flat track the focus
          // steps to a neighbour, on a dynamic one it crosses the scene.
          const reach = 1 + Math.floor(dyn * (PLANE_Z.length - 2) + 0.5);
          let pick = this._planeIndex;
          for (let guard = 0; guard < 8 && pick === this._planeIndex; guard++) {
            const step = 1 + Math.floor(Math.random() * reach);
            pick = (this._planeIndex + (Math.random() < 0.5 ? step : -step) + PLANE_Z.length * 2) % PLANE_Z.length;
          }
          this._startFocus(pick, FOCUS_BEATS, bpm);
        }
        this._lastPhrase = phrase;
      }

      // --- a known drop pulls the whole scene through in one beat
      const next = director && director.nextEvent;
      if (next && next.type === 'drop' && next.msUntil != null) {
        if (next.msUntil > 120) this._dropArmed = true;
        else if (this._dropArmed) {
          this._dropArmed = false;
          this._fromDepth = CAMERA_Z - PLANE_Z[PLANE_Z.length - 1];
          this._planeIndex = 0;
          this._toDepth = CAMERA_Z - PLANE_Z[0];
          this._focusT = 0;
          this._focusDur = Math.max(0.2, RACK_BEATS * 60 / Math.max(bpm, 40));
        }
      }
      if (this._forceRack && this._time > 3 && !this._rackDone) {
        this._rackDone = true;
        this._fromDepth = CAMERA_Z - PLANE_Z[PLANE_Z.length - 1];
        this._planeIndex = 0;
        this._toDepth = CAMERA_Z - PLANE_Z[0];
        this._focusT = 0;
        this._focusDur = Math.max(0.2, RACK_BEATS * 60 / Math.max(bpm, 40));
      }
      this._focusT = Math.min(1, this._focusT + deltaTime / this._focusDur);
    }

    // --- lateral drift, each plane at its own rate
    const q = this._driftPhase;
    this._near.points.position.set(Math.sin(q * 0.07) * DRIFT_BASE * 9,
                                   Math.cos(q * 0.053) * DRIFT_BASE * 6, 0);
    this._far.points.position.set(Math.sin(q * 0.041 + 1.7) * DRIFT_BASE * 5,
                                  Math.cos(q * 0.033 + 0.6) * DRIFT_BASE * 3.4, 0);
    this._back.position.set(Math.sin(q * 0.023) * DRIFT_BASE * 3,
                            Math.cos(q * 0.019) * DRIFT_BASE * 2, PLANE_Z[4]);
    // Foreground drift is applied in _fitCover, on top of the rim placement.

    // --- progress decides how many particles are alive
    const p = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._near.geo.setDrawRange(0, Math.round(NEAR_PARTICLES * (0.45 + 0.55 * p)));
    this._far.geo.setDrawRange(0, Math.round(FAR_PARTICLES * (0.40 + 0.60 * p)));

    // --- palette
    if (profile.artworkUrl && profile.artworkUrl !== this._artUrl) {
      this._artUrl = profile.artworkUrl;
      this._loadArtwork(profile.artworkUrl);
    }

    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null;
    const dom = palette && palette.dominantColors && palette.dominantColors.length ? palette.dominantColors : null;
    if (vivid && vivid !== this._lastVivid) {
      this._lastVivid = vivid;
      this._tintField(this._near, vivid);
      this._tintField(this._far, vivid);
      this._backUniforms.uGlowA.value.set(vivid[0]);
      this._backUniforms.uGlowB.value.set(vivid[vivid.length - 1]);
    }
    if (dom) this._backUniforms.uTint.value.set(dom[0]).multiplyScalar(0.40);

    this._particleUniforms.uTime.value = this._time;
  }

  // Bright, because a particle is only worth anything here once it is a bokeh.
  _tintField(field, vivid) {
    const attr = field.geo.getAttribute('aColor');
    const c = new THREE.Color();
    for (let i = 0; i < field.count; i++) {
      c.set(vivid[i % vivid.length]);
      const hsl = { h: 0, s: 0, l: 0 };
      c.getHSL(hsl);
      c.setHSL(hsl.h, Math.min(1, hsl.s * 0.85 + 0.18), 0.55 + field.rand() * 0.34);
      attr.setXYZ(i, c.r, c.g, c.b);
    }
    attr.needsUpdate = true;
  }

  _loadArtwork(url) {
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
      function (texture) {
        self._artwork = texture;
        self._coverUniforms.uArtwork.value = texture;
        self._coverUniforms.uHasArtwork.value = 1;
      },
      function () { /* keep whatever is already on the plane */ });
  }

  // The only place the renderer may be touched.
  _render(renderer) {
    const resized = this._sceneBuf.ensureSize(renderer);
    this._blurBuf.ensureSize(renderer);

    const w = this._sceneBuf.width, h = this._sceneBuf.height;
    const aspect = w / Math.max(1, h);
    if (this._innerCam.aspect !== aspect) {
      this._innerCam.aspect = aspect;
      this._innerCam.updateProjectionMatrix();
    }
    // Particle size is in pixels, so it has to follow the buffer, not the frame.
    this._particleUniforms.uPixelScale.value = h * 0.55;
    this._fitCover(aspect);

    const focus = this._focusDepth();
    this._blurUniforms.uFocusDepth.value = focus;
    this._blurUniforms.uAperture.value = this._aperture;
    this._blurUniforms.uAspect.value = aspect;
    this._compositeUniforms.uFocusDepth.value = focus;
    this._compositeUniforms.uAperture.value = this._aperture;
    this._compositeUniforms.uHeightPx.value = h;

    const prev = renderer.getRenderTarget();
    const prevClear = renderer.getClearAlpha();

    // 1. the scene, with depth in alpha
    renderer.setRenderTarget(this._sceneBuf.read);
    renderer.setClearAlpha(1);
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._innerCam);

    // 2. the disc blur, at half resolution
    this._blurUniforms.uScene.value = this._sceneBuf.read.texture;
    this._blurUniforms.uTexel.value.set(1 / w, 1 / h);
    this._passQuad.material = this._blurMat;
    renderer.setRenderTarget(this._blurBuf.read);
    renderer.render(this._passScene, this._passCam);

    renderer.setRenderTarget(prev);
    renderer.setClearAlpha(prevClear);
    this._compositeUniforms.uScene.value = this._sceneBuf.read.texture;
    this._compositeUniforms.uBlur.value = this._blurBuf.read.texture;
    if (resized) this._blurBuf.clear(renderer);
  }

  // The 3D cover has to land exactly under the DOM one, or the moment the
  // focus leaves this plane - the sharp overlay in front of its own blurred
  // copy - does not read.
  _fitCover(aspect) {
    const half = safeZoneUniforms.uSafeHalfSize.value;
    const centre = safeZoneUniforms.uSafeCenter.value;
    // The overlay box spans cover plus title and artist; the cover is the
    // square at its top, so its half-size is the smaller of the two.
    const side = Math.min(half.x, half.y);
    const cy = centre.y + half.y - side;
    const depth = CAMERA_Z - PLANE_Z[2];
    const halfH = depth * Math.tan(FOV * Math.PI / 360);
    const s = Math.max(0.1, side * halfH * 2);
    this._cover.scale.set(s, s, 1);
    this._cover.position.set(centre.x / Math.max(aspect, 0.001) * halfH * aspect, cy * halfH, PLANE_Z[2]);

    const backDepth = CAMERA_Z - PLANE_Z[4];
    const bh = backDepth * Math.tan(FOV * Math.PI / 360) * 2.3;
    this._back.scale.set(bh * aspect, bh, 1);

    // The near silhouettes, anchored just outside the frame at their own angle.
    const fh = (CAMERA_Z - PLANE_Z[0]) * Math.tan(FOV * Math.PI / 360);
    const fw = fh * aspect;
    const q = this._driftPhase;
    for (let i = 0; i < this._shapes.length; i++) {
      const m = this._shapes[i], d = m.userData.drift;
      const a = m.userData.angle, r = m.userData.radius;
      const sz = m.userData.span * fh;
      m.scale.set(sz * (0.8 + 0.5 * Math.abs(d[0])), sz, 1);
      m.position.set(
        Math.cos(a) * fw * r + Math.sin(q * 0.03 * d[2]) * DRIFT_BASE * 9 * d[0],
        Math.sin(a) * fh * r + Math.cos(q * 0.026 * d[2]) * DRIFT_BASE * 7 * d[1],
        PLANE_Z[0]);
    }
  }

  dispose() {
    this._scene.remove(this._display);
    this._display.geometry.dispose();
    this._display.material.dispose();
    this._passQuad.geometry.dispose();
    this._blurMat.dispose();
    this._near.geo.dispose();
    this._far.geo.dispose();
    this._near.points.material.dispose();
    this._back.geometry.dispose();
    this._back.material.dispose();
    this._cover.geometry.dispose();
    this._cover.material.dispose();
    for (let i = 0; i < this._shapes.length; i++) {
      this._shapes[i].geometry.dispose();
      this._shapes[i].material.dispose();
    }
    this._sceneBuf.dispose();
    this._blurBuf.dispose();
  }
}
